import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, liveStatusTranslationKey, notificationDeliveryTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import { DeliveryDashboard, DeliveryMetricBucket, SenderDomainReadiness } from './notification.models';
import { NotificationService } from './notification.service';
import { normalizeNotificationChannel } from './notification.utils';

@Component({
  standalone: true,
  selector: 'ain-notifications-delivery',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, AinFormFieldComponent,
    FormsModule, TableModule, ButtonModule, InputTextModule, InputNumberModule, SelectModule, ChartModule, JalaliDateInputComponent, AinCanAccessDirective
  ],
  template: `
    <ain-page [eyebrow]="t('notifications.eyebrow')" [title]="t('notifications.delivery.title')" [description]="t('notifications.delivery.description')">
      <ain-info-hint page-title-addon [titleKey]="'notifications.delivery.help.title'" [descriptionKey]="'notifications.delivery.help.description'"
        [bulletKeys]="['notifications.delivery.help.bullets.metrics','notifications.delivery.help.bullets.health','notifications.delivery.help.bullets.sender']" />

      <section class="card toolbar">
        <ain-form-field [label]="t('notifications.messages.filters.fromDate')"><ain-date-input [(ngModel)]="filters.fromDate" /></ain-form-field>
        <ain-form-field [label]="t('notifications.messages.filters.toDate')"><ain-date-input [(ngModel)]="filters.toDate" /></ain-form-field>
        <ain-form-field [label]="t('notifications.messages.filters.channel')"><p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="filters.channel" [showClear]="true" appendTo="body" /></ain-form-field>
        <ain-form-field [label]="t('notifications.messages.filters.provider')"><p-select [options]="providerOptions()" optionLabel="label" optionValue="value" [(ngModel)]="filters.providerConfigId" [showClear]="true" appendTo="body" /></ain-form-field>
        <button pButton type="button" [label]="t('catalog.action.search')" (click)="loadMetrics()"></button>
        <button pButton type="button" [label]="t('catalog.action.refresh')" (click)="load()"></button>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </section>

      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> }
      @else {
        <section class="metrics">
          <article class="metric-card"><span>{{ t('notifications.delivery.metrics.sent') }}</span><strong>{{ totals().sent }}</strong></article>
          <article class="metric-card"><span>{{ t('notifications.delivery.metrics.delivered') }}</span><strong>{{ totals().delivered }}</strong></article>
          <article class="metric-card"><span>{{ t('notifications.delivery.metrics.failed') }}</span><strong>{{ totals().failed }}</strong></article>
          <article class="metric-card warn"><span>{{ t('notifications.delivery.metrics.storm') }}</span><strong>{{ dashboard()?.stormProviderCount ?? 0 }}</strong></article>
        </section>

        <section class="card">
          <h3>{{ t('notifications.delivery.sections.trendChart') }}</h3>
          <p-chart type="line" [data]="chartData()" [options]="chartOptions" height="320px" />
        </section>

        <section class="card">
          <h3>{{ t('notifications.delivery.sections.metricsTable') }}</h3>
          <p-table [value]="metrics()" [paginator]="true" [rows]="12">
            <ng-template pTemplate="header"><tr><th>{{ t('notifications.delivery.columns.date') }}</th><th>{{ t('notifications.messages.filters.channel') }}</th><th>{{ t('notifications.delivery.columns.sent') }}</th><th>{{ t('notifications.delivery.columns.delivered') }}</th><th>{{ t('notifications.delivery.columns.failed') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row>
              <tr><td>{{ row.bucketDate }}</td><td>{{ channelLabel(row.channel) }}</td><td>{{ row.sentCount }}</td><td>{{ row.deliveredCount }}</td><td>{{ row.failedCount }}</td></tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('notifications.delivery.sections.senderReadiness') }}</h3>
            <ain-info-hint [titleKey]="'notifications.delivery.help.sender.title'" [descriptionKey]="'notifications.delivery.help.sender.description'" />
          </div>
          <div ainCanAccess [ainCanAccessPermission]="'notifications.delivery.manage'" class="grid">
            <ain-form-field [label]="t('notifications.delivery.fields.domain')"><input pInputText [(ngModel)]="senderForm.domain" /></ain-form-field>
            <ain-form-field [label]="t('notifications.messages.filters.channel')">
              <p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="senderForm.channel" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('notifications.messages.filters.provider')"><p-select [options]="providerOptions()" optionLabel="label" optionValue="value" [(ngModel)]="senderForm.providerConfigId" [showClear]="true" appendTo="body" /></ain-form-field>
            <ain-form-field [label]="t('notifications.delivery.fields.spf')"><p-select [options]="dnsStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="senderForm.spfStatus" appendTo="body" /></ain-form-field>
            <ain-form-field [label]="t('notifications.delivery.fields.dkim')"><p-select [options]="dnsStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="senderForm.dkimStatus" appendTo="body" /></ain-form-field>
            <ain-form-field [label]="t('notifications.delivery.fields.dmarc')"><p-select [options]="dnsStatusOptions" optionLabel="label" optionValue="value" [(ngModel)]="senderForm.dmarcStatus" appendTo="body" /></ain-form-field>
            <ain-form-field [label]="t('notifications.delivery.fields.warmupCap')"><p-inputNumber [(ngModel)]="senderForm.warmupDailyCap" [min]="0" /></ain-form-field>
          </div>
          <button ainCanAccess [ainCanAccessPermission]="'notifications.delivery.manage'" pButton type="button" [label]="t('common.save')" [loading]="savingSender()" (click)="saveSenderReadiness()"></button>

          <p-table [value]="senderRows()" [paginator]="true" [rows]="8" class="sender-table">
            <ng-template pTemplate="header"><tr><th>{{ t('notifications.delivery.fields.domain') }}</th><th>SPF</th><th>DKIM</th><th>DMARC</th><th>{{ t('notifications.delivery.columns.marketingBlocked') }}</th></tr></ng-template>
            <ng-template pTemplate="body" let-row>
              <tr><td>{{ row.domain }}</td><td>{{ row.spfStatus }}</td><td>{{ row.dkimStatus }}</td><td>{{ row.dmarcStatus }}</td><td>{{ row.marketingBlocked ? t('common.yes') : t('common.no') }}</td></tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:12px}
    .toolbar{display:flex;gap:8px;align-items:end;flex-wrap:wrap}.metrics{display:grid;gap:10px;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));margin-bottom:12px}
    .metric-card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;background:#fff;display:grid;gap:4px}
    .metric-card.warn{border-color:#f59e0b}.grid{display:grid;gap:10px;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));margin-bottom:10px}
    .section-title{display:flex;align-items:center;gap:8px;flex-wrap:wrap}.sender-table{margin-top:12px}
  `]
})
export class NotificationsDeliveryComponent implements OnInit {
  private readonly api = inject(NotificationService);
  private readonly i18n = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly savingSender = signal(false);
  readonly dashboard = signal<DeliveryDashboard | null>(null);
  readonly metrics = signal<DeliveryMetricBucket[]>([]);
  readonly senderRows = signal<SenderDomainReadiness[]>([]);
  readonly totals = signal({ sent: 0, delivered: 0, failed: 0 });
  readonly chartData = signal({ labels: [] as string[], datasets: [] as Array<{ label: string; data: number[]; borderColor: string; tension: number }> });
  readonly providerOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly chartOptions = { responsive: true, maintainAspectRatio: false };
  filters = { fromDate: null as string | null, toDate: null as string | null, channel: null as string | null, providerConfigId: null as string | null };

  senderForm = { domain: '', channel: 'Email', providerConfigId: null as string | null, spfStatus: 'Unknown', dkimStatus: 'Unknown', dmarcStatus: 'Unknown', warmupDailyCap: 0 };
  readonly channelOptions = [{ label: 'Email', value: 'Email' }, { label: 'SMS', value: 'Sms' }];
  readonly dnsStatusOptions = [
    { label: 'Unknown', value: 'Unknown' },
    { label: 'Verified', value: 'Verified' },
    { label: 'Failed', value: 'Failed' }
  ];

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.notificationDeliveryChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    void this.setupLiveSubscription();
    this.load(true);
  }

  t(key: string): string { return this.i18n.translate(key); }
  channelLabel(channel: unknown): string { return this.t(`notifications.enums.channel.${normalizeNotificationChannel(channel as never)}`); }

  load(showLoading = true): void {
    if (showLoading) this.loading.set(true);
    this.api.getDeliveryDashboard().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: dashboard => {
        this.dashboard.set(dashboard);
        this.providerOptions.set(dashboard.providers.map(item => ({ label: item.displayName, value: item.id })));
        this.applyMetrics(dashboard.metrics);
      }
    });
    this.api.listSenderReadiness().subscribe(rows => this.senderRows.set(rows));
  }

  loadMetrics(): void {
    this.api.listDeliveryMetrics(this.filters.fromDate, this.filters.toDate).subscribe(rows => this.applyMetrics(rows));
  }

  private applyMetrics(rows: DeliveryMetricBucket[]): void {
    const filtered = rows.filter(row =>
      (!this.filters.channel || normalizeNotificationChannel(row.channel) === this.filters.channel) &&
      (!this.filters.providerConfigId || row.providerConfigId === this.filters.providerConfigId));
    this.metrics.set(filtered);
    this.totals.set({
      sent: filtered.reduce((sum, row) => sum + row.sentCount, 0),
      delivered: filtered.reduce((sum, row) => sum + row.deliveredCount, 0),
      failed: filtered.reduce((sum, row) => sum + row.failedCount, 0)
    });
    const dates = [...new Set(filtered.map(row => row.bucketDate))].sort();
    const aggregate = (date: string, key: 'sentCount' | 'deliveredCount' | 'failedCount') =>
      filtered.filter(row => row.bucketDate === date).reduce((sum, row) => sum + row[key], 0);
    this.chartData.set({
      labels: dates,
      datasets: [
        { label: this.t('notifications.delivery.metrics.sent'), data: dates.map(date => aggregate(date, 'sentCount')), borderColor: '#2563eb', tension: 0.25 },
        { label: this.t('notifications.delivery.metrics.delivered'), data: dates.map(date => aggregate(date, 'deliveredCount')), borderColor: '#16a34a', tension: 0.25 },
        { label: this.t('notifications.delivery.metrics.failed'), data: dates.map(date => aggregate(date, 'failedCount')), borderColor: '#dc2626', tension: 0.25 }
      ]
    });
  }

  saveSenderReadiness(): void {
    if (!this.canManageDelivery()) return;
    this.savingSender.set(true);
    this.api.upsertSenderReadiness({
      domain: this.senderForm.domain,
      channel: this.senderForm.channel,
      spfStatus: this.senderForm.spfStatus,
      dkimStatus: this.senderForm.dkimStatus,
      dmarcStatus: this.senderForm.dmarcStatus,
      warmupDailyCap: this.senderForm.warmupDailyCap,
      providerConfigId: this.senderForm.providerConfigId
    }).pipe(finalize(() => this.savingSender.set(false))).subscribe({
      next: () => this.api.listSenderReadiness().subscribe(rows => this.senderRows.set(rows))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) return;
    await this.liveUpdateService.subscribe(notificationDeliveryTopic(tenantId));
  }

  private canManageDelivery(): boolean {
    const context = this.context.context();
    return (context?.roles ?? []).includes('SystemAdmin') ||
      (context?.permissions ?? []).some(item => item.key === 'notifications.delivery.manage');
  }
}
